import { Suspense } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { useSessionOptional } from '../../contexts/session-context';
import { Http404Lazy } from '../../pages/http404-lazy';

const suspenseFallback = (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            background: 'radial-gradient(#fff, #eee)'
        }}
    >
        <Typography color="text.secondary">…</Typography>
    </Box>
);

function errorSummary(error: unknown): string {
    if (isRouteErrorResponse(error)) {
        return error.data && typeof error.data === 'string'
            ? error.data
            : `${error.status} ${error.statusText}`.trim();
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Something went wrong.';
}

/**
 * Use as `errorElement` on routes so loader/action/render errors never show the
 * framework default screen.
 */
export function RouteErrorFallback() {
    const error = useRouteError();
    const navigate = useNavigate();
    const session = useSessionOptional();
    const showTechnicalDetails = session?.isAdmin === true;

    if (isRouteErrorResponse(error) && error.status === 404) {
        return (
            <Suspense fallback={suspenseFallback}>
                <Http404Lazy />
            </Suspense>
        );
    }

    const summary = errorSummary(error);
    const stack = error instanceof Error ? error.stack : undefined;
    const showStack = Boolean(showTechnicalDetails && stack);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                p: 2,
                background: 'radial-gradient(#fff, #eee)'
            }}
        >
            <Stack spacing={2} sx={{ maxWidth: 480, width: '100%' }}>
                <Typography variant="h5" component="h1">
                    Something went wrong
                </Typography>
                {showTechnicalDetails ? (
                    <Alert severity="error">{summary}</Alert>
                ) : (
                    <Typography color="text.secondary">
                        Please try again. If the problem continues, contact support.
                    </Typography>
                )}
                {showStack ? (
                    <Typography
                        component="pre"
                        variant="caption"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            maxHeight: 200,
                            overflow: 'auto',
                            bgcolor: 'action.hover',
                            p: 1,
                            borderRadius: 1
                        }}
                    >
                        {stack}
                    </Typography>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button variant="contained" onClick={() => navigate(0)}>
                        Try again
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate(0))}
                    >
                        Go back
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}
